import React, { useMemo } from 'react';
import { Table, Tag, Typography, Collapse } from 'antd';

const { Text } = Typography;

const safeJsonParse = (value) => {
  if (!value) return null;
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const toDate = (iso) => {
  if (!iso || typeof iso !== 'string') return null;
  const d = new Date(iso);
  // If iso is missing timezone, Date() may parse local; still better than nothing.
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDateTime = (iso) => {
  const d = toDate(iso);
  return d ? d.toLocaleString() : '-';
};

const durationMs = (startedAt, completedAt) => {
  const a = toDate(startedAt);
  const b = toDate(completedAt);
  if (!a || !b) return null;
  const ms = b.getTime() - a.getTime();
  return ms >= 0 ? ms : null;
};

const formatDuration = (ms) => {
  if (ms === null || ms === undefined) return '-';
  if (ms < 1000) return `${ms} ms`;
  const s = Math.round(ms / 100) / 10;
  return `${s} s`;
};

const pickSummary = (step) => {
  if (!step || typeof step !== 'object') return '';
  if (step.message) return String(step.message);
  const resultData = step.result_data;
  if (resultData && typeof resultData === 'object') {
    if (resultData.message) return String(resultData.message);
    if (resultData.error) return String(resultData.error);
  }
  return '';
};

const statusTag = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'completed' || s === 'success') return <Tag color="green">{status}</Tag>;
  if (s === 'failed' || s === 'error') return <Tag color="red">{status}</Tag>;
  if (s === 'running') return <Tag color="blue">{status}</Tag>;
  if (s === 'waiting') return <Tag color="gold">{status}</Tag>;
  return <Tag>{status || 'unknown'}</Tag>;
};

const ExecutionLogViewer = ({ executionLog, jobId = null }) => {
  const steps = useMemo(() => {
    const parsed = safeJsonParse(executionLog);
    const arr = Array.isArray(parsed) ? parsed : [];

    const filteredByJob = jobId ? arr.filter(s => s && s.job_id === jobId) : arr;
    const effective = (jobId && filteredByJob.length === 0) ? arr : filteredByJob;

    return effective.map((step, idx) => {
      const ms = durationMs(step.started_at, step.completed_at);
      return {
        key: `${step.job_id || 'job'}-${step.node_id || 'node'}-${idx}`,
        index: idx + 1,
        node: step.node_label || step.node_name || step.node_id,
        node_id: step.node_id,
        module: step.module_name,
        status: step.status,
        started_at: step.started_at,
        completed_at: step.completed_at,
        duration_ms: ms,
        summary: pickSummary(step),
        raw: step,
      };
    });
  }, [executionLog, jobId]);

  const columns = [
    {
      title: '#',
      dataIndex: 'index',
      width: 56,
    },
    {
      title: 'Node',
      dataIndex: 'node',
      ellipsis: true,
      render: (value, row) => (
        <div>
          <div>{value || '-'}</div>
          {row.node_id && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {row.node_id}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: 'Module',
      dataIndex: 'module',
      width: 180,
      ellipsis: true,
      render: (v) => v || '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: (v) => statusTag(v),
    },
    {
      title: 'Started',
      dataIndex: 'started_at',
      width: 190,
      render: (v) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{formatDateTime(v)}</span>,
    },
    {
      title: 'Duration',
      dataIndex: 'duration_ms',
      width: 110,
      render: (v) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{formatDuration(v)}</span>,
    },
    {
      title: 'Summary',
      dataIndex: 'summary',
      ellipsis: true,
      render: (v) => v || '-',
    },
  ];

  if (!steps.length) {
    return <Text type="secondary">No execution log entries yet.</Text>;
  }

  return (
    <Table
      size="small"
      columns={columns}
      dataSource={steps}
      pagination={false}
      scroll={{ y: 320 }}
      expandable={{
        expandedRowRender: (row) => {
          const raw = row.raw || {};
          const resultData = raw.result_data;
          return (
            <Collapse
              size="small"
              items={[
                {
                  key: 'details',
                  label: 'Step details',
                  children: (
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(raw, null, 2)}
                    </pre>
                  ),
                },
                resultData
                  ? {
                      key: 'result',
                      label: 'Result data',
                      children: (
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                          {JSON.stringify(resultData, null, 2)}
                        </pre>
                      ),
                    }
                  : null,
              ].filter(Boolean)}
            />
          );
        },
        rowExpandable: () => true,
      }}
    />
  );
};

export default ExecutionLogViewer;
